import { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal, MaskedLine } from "@/components/Kinetic";
import EditorialMarquee from "@/components/Marquee";
import { api } from "@/lib/api";

export default function Photos() {
  const [photos, setPhotos] = useState([]);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    api.get("/gallery").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  const step = useCallback(
    (dir) => setLightbox((i) => (i === null || photos.length === 0 ? null : (i + dir + photos.length) % photos.length)),
    [photos.length],
  );

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, step]);

  return (
    <main data-testid="gallery-page" className="pb-40 pt-36">
      <div className="mx-auto max-w-7xl px-6">
        <p className="mb-4 font-mono text-xs tracking-[0.4em] text-primary">THE VISUALS</p>
        <h1 data-testid="gallery-title" className="font-anton leading-[0.9]">
          <MaskedLine className="text-6xl text-white md:text-8xl" delay={0.1}>PHOTO</MaskedLine>
          <MaskedLine className="text-outline glow-blue text-6xl md:text-8xl" delay={0.25}>GALLERY</MaskedLine>
        </h1>
        <p className="mt-8 max-w-lg text-white/60">
          Official shots from the Southern Smoke camp. Tap any photo to view it full-screen — swipe or use the arrows.
        </p>
      </div>

      <EditorialMarquee className="mt-16" speed={40} items={["MIKE MILLZZ", "SOUTHERN SMOKE", "THE VISUALS"]} />

      <div className="mx-auto mt-16 max-w-7xl columns-2 gap-4 px-6 md:columns-3">
        {photos.map((p, i) => (
          <Reveal key={p.id} delay={(i % 3) * 0.08} className="mb-4 break-inside-avoid">
            <button
              data-testid={`gallery-photo-${i}`}
              onClick={() => setLightbox(i)}
              aria-label={`Open photo ${i + 1}`}
              className="group block w-full overflow-hidden border border-white/10 transition-colors duration-300 hover:border-primary/50"
            >
              <img
                src={p.url}
                alt={p.caption || `Mike Millzz gallery photo ${i + 1}`}
                loading="lazy"
                className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            </button>
            {p.caption && (
              <p data-testid={`gallery-caption-text-${i}`} className="mt-2 font-mono text-[10px] tracking-[0.2em] text-white/50">
                {p.caption}
              </p>
            )}
          </Reveal>
        ))}
        {photos.length === 0 && (
          <p data-testid="gallery-empty" className="py-20 text-center font-mono text-xs tracking-[0.3em] text-white/40">
            PHOTOS LOADING...
          </p>
        )}
      </div>

      <AnimatePresence>
        {lightbox !== null && photos[lightbox] && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setLightbox(null)}
            className="fixed inset-0 z-[90] flex items-center justify-center bg-black/95 p-6"
          >
            <button
              data-testid="lightbox-close"
              onClick={() => setLightbox(null)}
              aria-label="Close"
              className="absolute right-5 top-5 border border-white/20 p-3 text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary"
            >
              <X size={20} />
            </button>
            <button
              data-testid="lightbox-prev"
              onClick={(e) => { e.stopPropagation(); step(-1); }}
              aria-label="Previous photo"
              className="absolute left-4 top-1/2 -translate-y-1/2 border border-white/20 p-3 text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary md:left-8"
            >
              <ChevronLeft size={22} />
            </button>
            <motion.img
              key={lightbox}
              data-testid="lightbox-image"
              src={photos[lightbox].url}
              alt={`Mike Millzz photo ${lightbox + 1}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[82vh] max-w-full border border-white/15 object-contain"
            />
            <button
              data-testid="lightbox-next"
              onClick={(e) => { e.stopPropagation(); step(1); }}
              aria-label="Next photo"
              className="absolute right-4 top-1/2 -translate-y-1/2 border border-white/20 p-3 text-white/70 transition-colors duration-300 hover:border-primary hover:text-primary md:right-8"
            >
              <ChevronRight size={22} />
            </button>
            <div className="absolute bottom-6 flex flex-col items-center gap-2">
              {photos[lightbox].caption && (
                <p data-testid="lightbox-caption" className="font-mono text-xs tracking-[0.2em] text-white/85">
                  {photos[lightbox].caption}
                </p>
              )}
              <p data-testid="lightbox-counter" className="font-mono text-[10px] tracking-[0.3em] text-white/50">
                {lightbox + 1} / {photos.length}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
